const DESKTOP_QUERY = window.matchMedia('(min-width: 750px)');

function renderReviewText(text) {
  const wrapper = document.createElement('div');
  const parts = String(text || '').split(/\n+/);

  parts.forEach((part, index) => {
    if (index > 0) wrapper.append(document.createElement('br'));
    wrapper.append(document.createTextNode(part));
  });

  return wrapper;
}

function truncateAtWordBoundary(text, limit) {
  const normalized = String(text || '').replace(/\s+/g, ' ').trim();
  if (!normalized || normalized.length <= limit) return { preview: normalized, truncated: false };

  const words = normalized.split(' ');
  let preview = '';

  for (const word of words) {
    const next = preview ? `${preview} ${word}` : word;
    if (next.length > limit) break;
    preview = next;
  }

  if (!preview) {
    preview = normalized.slice(0, limit).trim();
  }

  return { preview, truncated: preview.length < normalized.length };
}

function updateReviewText(card, limit, label, onToggle) {
  const root = card.querySelector('.review-card__review-root');
  if (!(root instanceof HTMLElement)) return;
  const isExpanded = card.dataset.reviewExpanded === 'true';

  const reviewText = card.getAttribute('data-review-text') || '';
  const { preview, truncated } = truncateAtWordBoundary(reviewText, limit);

  root.replaceChildren();

  if (!truncated) {
    const review = document.createElement('div');
    review.className = 'review-card__review review-card__review--static';
    review.append(renderReviewText(reviewText));
    root.append(review);
    return;
  }

  const review = document.createElement('div');
  review.className = 'review-card__review review-card__review--expandable';
  review.dataset.expanded = String(isExpanded);

  if (!isExpanded) {
    const previewWrap = document.createElement('div');
    previewWrap.className = 'review-card__preview';

    const excerpt = document.createElement('span');
    excerpt.className = 'review-card__excerpt';
    excerpt.textContent = preview;

    const readMore = document.createElement('button');
    readMore.type = 'button';
    readMore.className = 'review-card__read-more';
    readMore.textContent = label;
    readMore.addEventListener('mousedown', (event) => {
      event.stopPropagation();
    });
    readMore.addEventListener('pointerdown', (event) => {
      event.stopPropagation();
    });
    readMore.addEventListener(
      'touchstart',
      (event) => {
        event.stopPropagation();
      },
      { passive: true }
    );
    readMore.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      card.dataset.reviewExpanded = 'true';
      if (typeof onToggle === 'function') onToggle();
    });

    previewWrap.append(excerpt, readMore);
    review.append(previewWrap);
  } else {
    const fullText = document.createElement('div');
    fullText.className = 'review-card__full-text';
    fullText.append(renderReviewText(reviewText));
    review.append(fullText);
  }

  root.append(review);
}

function getPageSize(slideCount) {
  return DESKTOP_QUERY.matches && slideCount > 3 ? 3 : 1;
}

function getPageCount(slideCount) {
  return Math.ceil(slideCount / getPageSize(slideCount));
}

function updatePagination(slideshow, pagination) {
  if (!(pagination instanceof HTMLElement)) return;

  const slideCount = Number.parseInt(slideshow.dataset.slideCount || '0', 10);
  const isDesktop = DESKTOP_QUERY.matches;
  const shouldDisable = isDesktop ? slideCount <= 3 : slideCount <= 1;
  const pageSize = getPageSize(slideCount);
  const pageCount = getPageCount(slideCount);

  if (shouldDisable || pageCount <= 1) {
    pagination.hidden = true;
    pagination.replaceChildren();
    return;
  }

  pagination.hidden = false;

  const currentSlide = typeof slideshow.current === 'number' ? slideshow.current : 0;
  const activePage = Math.floor(currentSlide / pageSize);
  const existingButtons = [...pagination.querySelectorAll('.review-slideshow__dot')];

  if (existingButtons.length !== pageCount) {
    pagination.replaceChildren();

    for (let index = 0; index < pageCount; index += 1) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'review-slideshow__dot';
      button.setAttribute('aria-label', `Go to review page ${index + 1}`);
      button.addEventListener('click', () => {
        slideshow.select(index * pageSize);
      });
      pagination.append(button);
    }
  }

  const buttons = pagination.querySelectorAll('.review-slideshow__dot');
  buttons.forEach((button, index) => {
    button.setAttribute('aria-selected', String(index === activePage));
  });
}

function updateReviewSlideshow(slideshow) {
  const slideCount = Number.parseInt(slideshow.dataset.slideCount || '0', 10);
  const isDesktop = DESKTOP_QUERY.matches;
  const shouldDisable = isDesktop ? slideCount <= 3 : slideCount <= 1;
  const previewCharacterLimit = Number.parseInt(slideshow.dataset.previewCharacterLimit || '180', 10);
  const readMoreLabel = slideshow.dataset.readMoreLabel || 'Read more';
  const paginationId = slideshow.getAttribute('id');
  const pagination = paginationId
    ? document.querySelector(`[data-review-pagination-for="${paginationId}"]`)
    : null;

  slideshow.setAttribute('disabled', String(shouldDisable));
  updatePagination(slideshow, pagination);

  const cards = [...slideshow.querySelectorAll('.review-card')];
  if (!cards.length) return;

  const rerun = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        updateReviewSlideshow(slideshow);
      });
    });
  };

  for (const card of cards) {
    updateReviewText(card, previewCharacterLimit, readMoreLabel, rerun);
  }

  for (const card of cards) {
    card.style.removeProperty('--review-card-height');
    card.style.removeProperty('--review-bubble-height');
  }

  const bubbles = cards
    .map((card) => card.querySelector('.review-card__bubble'))
    .filter((bubble) => bubble instanceof HTMLElement);

  const tallestBubble = bubbles.reduce((max, bubble) => Math.max(max, bubble.offsetHeight), 0);

  if (tallestBubble) {
    for (const card of cards) {
      card.style.setProperty('--review-bubble-height', `${tallestBubble}px`);
    }
  }

  const tallest = cards.reduce((max, card) => Math.max(max, card.offsetHeight), 0);
  if (!tallest) return;

  for (const card of cards) {
    card.style.setProperty('--review-card-height', `${tallest}px`);
  }
}

function attachReviewSlideshow(slideshow) {
  const run = () => updateReviewSlideshow(slideshow);
  run();

  const resizeObserver = new ResizeObserver(() => {
    requestAnimationFrame(run);
  });

  const cards = slideshow.querySelectorAll('.review-card');
  for (const card of cards) {
    resizeObserver.observe(card);
  }

  slideshow.addEventListener('slideshow:select', run);
  DESKTOP_QUERY.addEventListener('change', run);
  window.addEventListener('load', run, { once: true });
}

const slideshows = document.querySelectorAll('[data-review-slideshow]');
for (const slideshow of slideshows) {
  attachReviewSlideshow(slideshow);
}
